import { cloneDeep, isEqual } from 'lodash';

/**
 * Performs a deep equality comparison between two values
 * @param obj1 First value to compare
 * @param obj2 Second value to compare
 * @returns boolean indicating if the values are deeply equal
 */
export function deepEqual(obj1: DeepEqualType, obj2: DeepEqualType): boolean {
  return isEqual(obj1, obj2);
}

export function deepClone<T>(obj: T): T {
  return cloneDeep(obj);
}

/**
 * Recursively removes specified fields from an object or array
 * @param obj Object or array to process
 * @param fieldsToIgnore Fields to remove
 * @returns Processed object with specified fields removed
 */
function removeFieldsRecursively<T>(obj: T, fieldsToIgnore: string[]): T {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => removeFieldsRecursively(item, fieldsToIgnore)) as unknown as T;
  }

  // Handle objects
  const result = { ...obj };
  fieldsToIgnore.forEach(field => {
    delete (result as any)[field];
  });

  // Process nested objects
  Object.keys(result).forEach(key => {
    const value = (result as any)[key];
    if (value && typeof value === 'object') {
      (result as any)[key] = removeFieldsRecursively(value, fieldsToIgnore);
    }
  });

  return result;
}

/**
 * Compares two objects for equality while ignoring specified fields
 * @param obj1 First object to compare
 * @param obj2 Second object to compare
 * @param fieldsToIgnore Array of field names to ignore during comparison
 * @returns boolean indicating if the objects are equal (ignoring specified fields)
 */
export function compareIgnoringFields<T>(obj1: T, obj2: T, fieldsToIgnore: string[]): boolean {
  if (!obj1 || !obj2) return obj1 === obj2;
  
  // Create deep copies of both objects
  const obj1Copy = deepClone(obj1);
  const obj2Copy = deepClone(obj2);
  
  // Remove the specified fields from both objects recursively
  const filteredObj1 = removeFieldsRecursively(obj1Copy, fieldsToIgnore);
  const filteredObj2 = removeFieldsRecursively(obj2Copy, fieldsToIgnore);
  
  // Compare the filtered objects
  return deepEqual(filteredObj1, filteredObj2);
}

export type DeepEqualType = 
  | string
  | number
  | boolean
  | null
  | undefined
  | Date
  | Array<any>
  | { [key: string]: any }
  | object;


  /**
   * Generates a unique ID for an object based on its properties
   * Sanitizes the ID to ensure it's valid for HTML id attributes
   * @param object - The object to generate an ID for
   * @returns A unique string ID safe for HTML
   */
  export function getObjectId(object: any): string {
    let baseId = object.id || object.name || Math.random().toString(36).substring(2, 11);
    
    // Sanitize the ID: remove/replace invalid characters for HTML id attribute
    // Valid characters: letters, digits, hyphens, underscores
    // Replace invalid chars with underscore
    return baseId
      .toString()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9\-_]+/g, '_');
  }
